// The HUD's own-hero stats block: current values with every modifier core applies, mirrored from core's
// own formulas (game.ts speedOf with its ground factor, artifacts.ts maxHpOf/attackSpeedMultOf/passiveMoveSpeedBonusOf, the
// basic attack's dmg buff in Game.damage). Pure: `statInputsOf` reads a GameView, `heroStats` turns that
// into display rows, and hud.ts only writes the DOM when the result's markup changes.
import { SKILLS } from '../core/skills.js';
import { ARTIFACTS } from '../core/artifacts.js';
import { MONSTERS, type MonsterKind } from '../core/monsters.js';
import type { HeroDef, HeroKind } from '../core/heroes.js';
import type { HeroState } from '../core/game.js';
import type { ArtifactLine, RelicLine } from '../core/commands.js';
import { RELICS } from '../core/relics.js';
import type { GameView } from './gameView.js';
import { t } from './i18n/index.js';
import { artifactName, className, heroName, monsterName, passiveName, relicName, skillName } from './i18n/names.js';

export type StatInputs = {
  kind: HeroKind;
  def: HeroDef;
  hp: number;
  held: readonly (ArtifactLine | null)[];
  /** The own team's relic (GameView.relics); absent = none. */
  relic?: RelicLine | null;
  /** Own team's monster buffs still running, seconds left. */
  monsterBuffs: Partial<Record<MonsterKind, number>>;
  slows: readonly { pct: number; left: number }[];
  haste: readonly { val: number; left: number }[];
  /** Сделка's damage buff — the only producer of core's 'dmg' buff, so a boolean pins its value. */
  enraged: boolean;
  state: HeroState;
  stunLeft: number;
  shielded: boolean;
  brandFor: number;
  cloakLeft: number;
  /** Сошка-adjusted basic-attack range (GameView.attackRangeOf, offline and online alike). */
  attackRange: number;
  /** Walking-speed factor of the ground under the hero (a ford: 0.8), GameView.groundSpeedAt. */
  ground: number;
  /** Seconds since the hero last took damage, capped (`you.effects.quietFor`); absent = out of combat. */
  quietFor?: number;
  /* Астрал — optional, absent = off (GameView.statusOf's fields of the same names). */
  forced?: 'charm' | 'fear' | null;
  forcedLeft?: number;
  confused?: boolean;
  sleepless?: boolean;
  invisible?: boolean;
  silhouette?: boolean;
};

export function statInputsOf(view: GameView): StatInputs {
  const p = view.player!, st = view.statusOf(p), fx = view.you.effects, tb = view.teamBuffs[p.team];
  const monsterBuffs: Partial<Record<MonsterKind, number>> = {};
  for (const k of Object.keys(tb) as MonsterKind[]) if (tb[k] !== null) monsterBuffs[k] = tb[k]!;
  return {
    kind: p.kind, def: p.def, hp: p.hp,
    held: [p.artifacts.E?.line ?? null, p.artifacts.R?.line ?? null],
    relic: view.relics[p.team],
    monsterBuffs,
    slows: fx?.slows ?? [], haste: fx?.haste ?? [],
    enraged: st.enraged, state: p.state,
    stunLeft: Math.max(0, p.stunUntil - view.t),
    shielded: st.shielded, brandFor: st.brandFor,
    cloakLeft: fx ? fx.cloakFor : st.cloaked ? Infinity : 0,
    attackRange: view.attackRangeOf(p),
    ground: view.groundSpeedAt(p.pos.x, p.pos.z),
    ...(fx?.quietFor !== undefined ? { quietFor: fx.quietFor } : {}),
    forced: st.forced ?? null, forcedLeft: st.forcedFor ?? 0,
    confused: !!st.confused, sleepless: !!st.sleepless, invisible: !!st.invisible, silhouette: !!st.silhouette,
  };
}

export type Tone = 'up' | 'down' | '';
export type StatRow = { label: string; value: string; tone: Tone; breakdown: string };
export type StatusChip = { id: string; label: string; left: number | null };
export type HeroStatsVM = { name: string; cls: string; hp: number; maxHp: number; hpTone: Tone; hpBreakdown: string; rows: StatRow[]; statuses: StatusChip[] };

const pct = (v: number) => `${v > 0 ? '+' : '−'}${Math.round(Math.abs(v) * 100)}%`;
const num = (v: number, digits: number) => { const t = v.toFixed(digits); return t.includes('.') ? t.replace(/\.?0+$/, '') : t; };
type Part = { label: string; text: string; sign: number };
function row(label: string, base: number, value: number, digits: number, parts: Part[]): StatRow {
  // Tint by what is displayed: 4.57 against a base of 4.6 reads "4.6", and must not show as a penalty.
  const shown = num(value, digits), baseShown = num(base, digits);
  const tone: Tone = shown === baseShown ? '' : Number(shown) > Number(baseShown) ? 'up' : 'down';
  const breakdown = [t('stats.base', { n: baseShown }), ...parts.map(p => `${p.label} ${p.text}`)].join(' · ');
  return { label, value: shown, tone, breakdown };
}

export function heroStats(i: StatInputs): HeroStatsVM {
  const { def } = i;
  const holds = (line: ArtifactLine) => i.held.includes(line);
  const boon = { hpPct: 0, moveSpeed: 0, atkSpeed: 0 };
  const boonParts: Record<'hpPct' | 'moveSpeed' | 'atkSpeed', Part[]> = { hpPct: [], moveSpeed: [], atkSpeed: [] };
  for (const kind of Object.keys(i.monsterBuffs) as MonsterKind[]) {
    const b = MONSTERS[kind].buff;
    for (const k of ['hpPct', 'moveSpeed', 'atkSpeed'] as const) {
      if (!b[k]) continue;
      boon[k] += b[k];
      boonParts[k].push({ label: monsterName(kind), text: pct(b[k]), sign: 1 });
    }
  }

  // maxHpOf
  const shoulder = holds('shoulder') ? ARTIFACTS.shoulder.hp : 0;
  const maxHp = def.hp * (1 + boon.hpPct) + shoulder;
  const hpParts = [...boonParts.hpPct, ...(shoulder ? [{ label: artifactName('shoulder'), text: `+${shoulder}`, sign: 1 }] : [])];
  const hpRow = row('', def.hp, maxHp, 0, hpParts);

  // Game.damage: amount *= max(1, buffVal(src, 'dmg'))
  const mult = i.enraged ? SKILLS.devil.W.mult : 1;
  const dmg = row(t('stats.dmg'), def.dmg, def.dmg * mult, 1, i.enraged ? [{ label: skillName('devil', 'W'), text: `×${mult}`, sign: 1 }] : []);

  // attackSpeedMultOf: cd = attackCd / mult
  const whet = holds('whetstone') ? ARTIFACTS.whetstone.atkSpeed : 0;
  const horn = i.relic === 'wildHorn' ? RELICS.wildHorn : null;  // Рог Дикой Охоты: game.ts attackCdOf/speedOf
  const hornAtk = horn ? horn.atkSpeed : 0, hornMove = horn ? horn.moveSpeed : 0;
  const aps = row(t('stats.aps'), 1 / def.attackCd, (1 + whet + boon.atkSpeed + hornAtk) / def.attackCd, 2, [
    ...(whet ? [{ label: artifactName('whetstone'), text: pct(whet), sign: 1 }] : []), ...boonParts.atkSpeed,
    ...(hornAtk ? [{ label: relicName('wildHorn'), text: pct(hornAtk), sign: 1 }] : []),
  ]);

  // Сошка: 1s standing still lengthens the arquebusier's own basic-attack range.
  const rangeBonus = i.attackRange - def.range;
  const range = row(t('stats.range'), def.range, i.attackRange, 1,
    rangeBonus > 0.001 ? [{ label: passiveName('arquebusier'), text: `+${num(rangeBonus, 1)}`, sign: 1 }] : []);

  // speedOf: speed * (1 - slow) * (1 - self) * ground * (1 + haste + passiveMoveSpeedBonusOf)
  const slow = Math.max(0, ...i.slows.map(s => s.pct));
  const self = i.state === 'brace' ? SKILLS.rhino.W.slow : i.state === 'charging' ? SKILLS.arquebusier.Q.slow : 0;
  const haste = Math.max(0, ...i.haste.map(h => h.val));
  // Сапоги гонца only out of combat (passiveMoveSpeedBonusOf's own `quiet` rule).
  const boots = holds('boots') && (i.quietFor ?? Infinity) >= ARTIFACTS.boots.quiet ? ARTIFACTS.boots.moveSpeed : 0;
  const speed = row(t('stats.speed'), def.speed, def.speed * (1 - slow) * (1 - self) * i.ground * (1 + haste + boots + boon.moveSpeed + hornMove), 1, [
    ...(boots ? [{ label: artifactName('boots'), text: pct(boots), sign: 1 }] : []),
    ...(hornMove ? [{ label: relicName('wildHorn'), text: pct(hornMove), sign: 1 }] : []),
    ...boonParts.moveSpeed,
    ...(haste ? [{ label: t('stats.haste'), text: pct(haste), sign: 1 }] : []),
    // Slows multiply the whole speed (speedOf), unlike the additive bonuses above.
    ...(slow ? [{ label: t('stats.slow'), text: `×${num(1 - slow, 2)}`, sign: -1 }] : []),
    ...(self ? [{ label: i.state === 'brace' ? skillName('rhino', 'W') : t('stats.charging'), text: `×${num(1 - self, 2)}`, sign: -1 }] : []),
    ...(i.ground !== 1 ? [{ label: t('stats.ford'), text: `×${num(i.ground, 2)}`, sign: -1 }] : []),
  ]);

  const maxLeft = (xs: readonly { left: number }[]) => (xs.length ? Math.max(...xs.map(x => x.left)) : 0);
  const statuses: StatusChip[] = [];
  const add = (id: string, label: string, on: boolean, left: number | null) => { if (on) statuses.push({ id, label, left: left !== null && Number.isFinite(left) ? left : null }); };
  add('stun', t('status.stun'), i.stunLeft > 0, i.stunLeft);
  add('charm', t('status.charm'), i.forced === 'charm', i.forcedLeft ?? null);
  add('fear', t('status.fear'), i.forced === 'fear', i.forcedLeft ?? null);
  add('delusion', t('status.delusion'), !!i.confused, null);
  add('sleepless', t('status.sleepless'), !!i.sleepless, null);
  add('stealth', t('status.stealth'), !!i.invisible, null);
  add('silhouette', t('status.silhouette'), !!i.silhouette, null);
  add('slow', t('stats.slow'), slow > 0, maxLeft(i.slows.filter(s => s.pct > 0)));
  add('brand', artifactName('brand'), i.brandFor > 0, i.brandFor);
  add('shield', t('status.shield'), i.shielded, null);
  add('cloak', artifactName('cloak'), i.cloakLeft > 0, i.cloakLeft);
  add('haste', t('stats.haste'), haste > 0, maxLeft(i.haste));
  add('pact', skillName('devil', 'W'), i.enraged, null);
  for (const kind of ['behemoth', 'griffin'] as const) add(kind, monsterName(kind), i.monsterBuffs[kind] !== undefined, i.monsterBuffs[kind] ?? null);

  return {
    name: heroName(i.kind), cls: className(def.cls),
    hp: Math.max(0, i.hp), maxHp, hpTone: hpRow.tone, hpBreakdown: hpRow.breakdown,
    rows: [dmg, aps, range, speed], statuses,
  };
}

const mmss = (s: number) => { const c = Math.ceil(s); return `${Math.floor(c / 60)}:${String(c % 60).padStart(2, '0')}`; };

/** Stat rows and status chips are separate markups, so a chip's timer ticking doesn't rebuild the rows
    (which would drop a row's hover breakdown every second). The HP bar is separate too (stable elements,
    hud.ts's damage-trail updater). */
export function heroStatsRowsHtml(vm: HeroStatsVM): string {
  return vm.rows.map(r => `<div class="ps-row${r.tone ? ' ' + r.tone : ''}"><span>${r.label}</span><b>${r.value}</b>`
    + (r.tone ? `<em class="ps-tip">${r.breakdown}</em>` : '') + '</div>').join('');
}
export function heroStatusHtml(vm: HeroStatsVM): string {
  const chips = vm.statuses.map(s => `<span class="ps-chip ${s.id}">${s.label}${s.left !== null ? ` <i>${mmss(s.left)}</i>` : ''}</span>`).join('');
  return chips ? `<div class="ps-status">${chips}</div>` : '';
}
